import { createTheme } from "@mui/material/styles";

export const lightTheme = createTheme({
  palette: {
    mode: "light", // This enables the light theme
    primary: {
      main: "#2196f3", // Light blue representing water and trust
    },
    secondary: {
      main: "#ff9800", // Orange for a contrasting call-to-action
    },
    background: {
      default: "#f4f4f9", // Light background for a clean feel
      paper: "#ffffff", // Pure white for cards and form backgrounds
    },
    text: {
      primary: "#333333", // Dark text for readability
      secondary: "#666666", // Muted text for less important elements
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', // Modern, professional font
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      color: "#333", // Primary text color
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 600,
      color: "#333",
    },
    body1: {
      fontSize: "1rem",
      color: "#666",
    },
    button: {
      textTransform: "none", // Make buttons look clean without all caps
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px", // Rounded buttons for a modern look
          padding: "12px 24px",
          fontSize: "1rem",
        },
        containedPrimary: {
          backgroundColor: "#2196f3", // Primary blue for buttons
          "&:hover": {
            backgroundColor: "#1976d2", // Darker blue on hover
          },
        },
        containedSecondary: {
          backgroundColor: "#ff9800", // Orange for secondary buttons
          "&:hover": {
            backgroundColor: "#fb8c00", // Darker orange on hover
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff", // White background for form inputs
          borderRadius: "4px", // Slight rounding
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px", // Rounded corners for cards
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)", // Subtle shadow for depth
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          transition: "background 0.7s ease-out",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(30, 30, 30, 0.2)", // Base opacity at 0.4
          mixBlendMode: "exclusion",
          backdropFilter: "blur(10px)", // Blur effect for a sleek look
          transition: "background 0.7s ease-out",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: "background 0.7s ease-out",
          opacity: 1,
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: "dark", // Dark theme enabled
    primary: {
      main: "#1976d2", // Deeper blue for more professional look
    },
    secondary: {
      main: "#ff8c00", // Slightly muted orange for contrast
    },
    background: {
      default: "#121212", // Dark background for overall app
      paper: "#1e1e1e", // Slightly lighter dark color for cards, forms, etc.
    },
    text: {
      primary: "#ffffff", // White text for strong contrast
      secondary: "#e0e0e0", // Slightly lighter text for readability
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', // Same modern font
    h1: {
      fontSize: "2.25rem", // Reduced slightly for modern design
      fontWeight: 700,
      color: "#ffffff", // White for headers to stand out
    },
    h2: {
      fontSize: "1.75rem", // Reduced for consistency
      fontWeight: 600,
      color: "#ffffff", // White for sub-headers
    },
    body1: {
      fontSize: "0.95rem", // Slightly smaller for readability
      color: "#b0b0b0", // Muted text for regular body text
    },
    button: {
      textTransform: "none", // Keeping buttons clean without all caps
      color: "#ffffff", // White text for strong contrast on buttons
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px", // Rounded buttons for consistency
          padding: "12px 24px",
          fontSize: "1rem",
          transition: "background 0.3s ease-out, box-shadow 0.3s ease", // Shorter transition for snappier feel
        },
        containedPrimary: {
          backgroundColor: "#1976d2", // Deeper blue for primary buttons
          color: "#ffffff", // White text for contrast
          "&:hover": {
            backgroundColor: "#1565c0", // Darker blue on hover
          },
        },
        containedSecondary: {
          backgroundColor: "#ff8c00", // Muted orange for secondary buttons
          color: "#000", // White text for contrast
          "&:hover": {
            backgroundColor: "#f57c00", // Darker orange on hover
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e1e", // Dark background for form inputs
          color: "#ffffff", // White text in form inputs
          borderRadius: "4px",
          transition: "background 0.3s ease-out, box-shadow 0.3s ease", // Shortened for faster feedback
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#b0b0b0", // Light border for inputs
            },
            "&:hover fieldset": {
              borderColor: "#2196f3", // Blue border on hover for focus
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2196f3", // Blue border when focused
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px", // Rounded corners for a softer look
          backgroundColor: "#1e1e1e", // Dark background for cards
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.6)", // Slightly stronger shadow for depth
          transition: "background 0.3s ease-out, box-shadow 0.3s ease", // Faster transition for interactivity
          border: "1px solid rgba(255, 255, 255, 0.1)", // Subtle border for card definition
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(30, 30, 30, 0.2)", // Base opacity at 0.4
          mixBlendMode: "luminosity",
          backdropFilter: "blur(10px)", // Blur effect for a sleek look
          transition: "background-color 0.3s ease", // Smooth transition for opacity changes
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: "background 0.7s ease-out",
          opacity: 1,
        },
      },
    },
  },
});
